import { AbilityBuilder, createMongoAbility, type MongoAbility } from '@casl/ability';
import type { DecodedJwt } from '@/features/auth/types';

/**
 * `readAll` è il perimetro org-wide introdotto dal BE il 06-08-2026 (`LeaveRequest.readAll`,
 * `Attendance.readAll`, `TimesheetEntry.readAll`, `PlanningEntry.readAll`): non è un permesso
 * più forte di `read`, è **lo stesso permesso su un insieme di righe più largo**. Per le
 * decisioni di perimetro usa `canSeeAll()` in `claims.ts`, che replica la formula del server;
 * qui sta solo perché il claim arriva nella stessa lista e va tipizzato.
 */
export type Action = 'create' | 'read' | 'readAll' | 'update' | 'delete' | 'manage' | 'approve' | 'export' | 'impersonate' | 'pay';
export type Subject = string;
export type AppAbility = MongoAbility<[Action, Subject]>;

/**
 * Le azioni che un claim CRUD può portare. Serve a distinguere `ExpenseReport.approve` — che è una
 * coppia soggetto-azione — da `billing.payment.manage`, che è un interruttore col punto dentro.
 */
const CASL_ACTIONS = new Set<string>([
  'create', 'read', 'readAll', 'update', 'delete', 'manage', 'approve', 'export', 'impersonate', 'pay',
]);

export function buildAbilityFromJwt(decoded: DecodedJwt | null): AppAbility {
  const { can, build } = new AbilityBuilder<AppAbility>(createMongoAbility);

  if (!decoded) return build();

  const isSuper = decoded.superadmin === 'True';
  if (isSuper) {
    can('manage', 'all');
    return build();
  }

  const claims = Array.isArray(decoded.claims) ? decoded.claims : [];
  for (const claim of claims) {
    /*
     * Solo i claim CRUD diventano regole CASL, e il filtro non è pignoleria.
     *
     * Prima qui c'era `claim.split('.', 2)` su **ogni** stringa della lista, e la lista non
     * contiene solo CRUD: ci sono i funzionali puntati del BE, che hanno tre e quattro segmenti.
     * `hr.protected.sensitive.read` diventava `can('protected', 'hr')`, `billing.payment.manage`
     * diventava `can('payment', 'billing')`, `warehouse.operate` → `can('operate', 'warehouse')`:
     * regole che non corrispondono a nessun soggetto esistente, in un'ability che tre rotte
     * interrogano davvero. Non hanno mai concesso niente per caso — nessuno chiede
     * `can('protected','hr')` — ma erano decine di regole finte in un oggetto che si legge per
     * capire cosa una persona può fare, e la prossima collisione sarebbe stata questione di nomi.
     *
     * I funzionali si leggono con `hasClaim(claims, CLAIM.x)` da `claims.ts`: sono interruttori,
     * non coppie soggetto-azione, e forzarli in questa forma è ciò che li rendeva illeggibili.
     */
    const point = claim.indexOf('.');
    if (point <= 0) continue;
    const subject = claim.slice(0, point);
    const action = claim.slice(point + 1);
    /* Il soggetto è una classe .NET (PascalCase) e l'azione una di quelle del tipo `Action`:
       insieme escludono i puntati in minuscolo senza doverli elencare. */
    if (!/^[A-Z][A-Za-z0-9]*$/.test(subject)) continue;
    if (!CASL_ACTIONS.has(action)) continue;
    can(action as Action, subject);
  }
  // Always allow reading own user/preferences
  can('read', 'CurrentUser');
  return build();
}
